import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { AttachmentService } from '../attachment.service';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { QuillImagePasteService } from './quill-image-paste.service';
import { EmojiBlot } from '@sb-tools/typescript/services/emoji-blot';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { CustomBlockQuote } from '@sb-tools/typescript/services/quill/custom-blockquote';
import { isPlatformBrowser } from '@angular/common';

const FIlE_MAX_SIZE_25MB = 25 * 1000 * 1000;

@Injectable({
    providedIn: 'root',
})
export class QuillHelperService implements OnDestroy {
    editedReplyId = null;

    htmlQuillConfig = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'], // toggled buttons

            [{ list: 'ordered' }, { list: 'bullet' }],

            [{ header: [1, 2, 3, 4, 5, 6, false] }],

            [{ color: [] }, { background: [] }], // dropdown with defaults from theme

            [{ align: [] }],

            ['clean', 'blockquote'], // remove formatting button

            // remove image controls on mobile devices because it doesnt work in safari ios
            ['link', ...(HelperService.isMobileOrTabletDevice() ? [] : ['image'])],
        ],
        imageDrop: {
            saveToServer: (quill, file) => {
                this.saveToServer(quill, file);
            },
        },
        imageResize: {
            displaySize: true,
        },
    };

    textQuillConfig = {
        toolbar: false,
        imageDrop: {
            saveToServer: (quill, file) => {
                this.saveToServer(quill, file);
            },
        },
        imageResize: {
            displaySize: true,
        },
    };

    customToolbarQuillConfig = {
        imageDrop: {
            saveToServer: (quill, file) => {
                this.saveToServer(quill, file);
            },
        },
        imageResize: {
            displaySize: true,
        },
    };

    private unsubscriber = new Subject<void>();

    constructor(
        private attachmentService: AttachmentService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        @Inject(PLATFORM_ID) private platformId: string,
    ) {}

    checkQuillEmpty(quillEditor) {
        if (quillEditor) {
            if ((quillEditor.getContents()['ops'] || []).length !== 1) {
                return false;
            }
            return quillEditor.getText().trim().length === 0;
        }

        return null;
    }

    convertImageSizeFromStylesToAttributes(text) {
        const div = document.createElement('div');
        div.innerHTML = text;

        const images = Array.from(div.getElementsByTagName('img'));

        if (images.length) {
            images.forEach((image) => {
                if (!image.getAttribute('width') && image.style.width) {
                    image.setAttribute('width', image.style.width);
                }

                if (!image.getAttribute('height') && image.style.height) {
                    image.setAttribute('height', image.style.height);
                }
            });
        }
        return div.innerHTML;
    }

    getNoteQuillConfig(linebreakHandler) {
        return {
            ...this.customToolbarQuillConfig,
            keyboard: {
                bindings: {
                    linebreak: {
                        key: 'Enter',
                        shortKey: true, // cmd or ctrl key
                        handler: () => {
                            linebreakHandler();
                        },
                    },
                },
            },
        };
    }

    async initQuillRegisters(): Promise<void> {
        if (isPlatformBrowser(this.platformId)) {
            const QuillModule = await import('quill');
            const Quill = QuillModule.default;
            const Block = Quill.import('blots/block') as any;
            Block.tagName = 'div';
            Quill.register(Block, true);
            const align = Quill.import('attributors/style/align') as any;
            Quill.register(align, true);
            Quill.register(Quill.import('attributors/style/background') as any, true);
            Quill.register(Quill.import('attributors/style/color') as any, true);
            Quill.register(Quill.import('attributors/style/direction') as any, true);
            Quill.register(Quill.import('attributors/style/font') as any, true);
            Quill.register('modules/imageDrop', QuillImagePasteService);
            Quill.register({ 'formats/emoji': EmojiBlot }, true);
            const ImageResizeModule = await import('quill-image-resize');
            const ImageResize =
                ImageResizeModule.ImageResize || ImageResizeModule.default?.default || ImageResizeModule.default;
            if (ImageResize) {
                Quill.register('modules/imageResize', ImageResize);
            }
            Quill.register(CustomBlockQuote);
        }
    }

    removeResizeCursorFromImages(tmpString) {
        if (tmpString) {
            return tmpString.replace(
                /<img(.)*style=("|')(.)*(cursor: (nwse-resize)|(nesw-resize))(.)*("|')(.)*>$/gi,
                (fullMatch) => {
                    return fullMatch.replace(/cursor: ((nwse-resize)|(nesw-resize))/, '');
                },
            );
        } else {
            return tmpString;
        }
    }

    /**
     * Step1. select local image
     *
     */
    selectLocalImage(editor) {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        // Listen upload local image and save to server
        input.oninput = () => {
            const file = input.files[0];

            // file type is only image.
            if (/^image\//.test(file.type)) {
                this.saveToServer(editor, file);
            } else {
                console.warn('You could only upload images.');
            }
        };
    }

    saveAsAttachment(attachments, file: File) {
        if (this.isFileExceedLimit(file.size)) {
            return;
        }

        this.attachmentService
            .uploadTicketAttachment(file)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                attachments.push(data);
            });
    }

    saveToServer(editor, file: File) {
        if (this.isFileExceedLimit(file.size)) {
            return;
        }

        this.attachmentService
            .uploadFile(file)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.insertToEditor(editor, data.fileUrl);
            });
    }

    isFileExceedLimit(size) {
        if (size > FIlE_MAX_SIZE_25MB) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('File is too big. Max file size: 25MB.'),
            );
            return true;
        } else {
            return false;
        }
    }

    insertToEditor(editor, url: string) {
        // push image url to rich editor.
        const range = editor.getSelection();
        editor.insertEmbed(range.index, 'image', url, 'user');
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
